/**
 * Sprint C — Global read-only / billing-required banner.
 *
 * Renders a sticky banner at the top of the app shell when the current
 * tenant's subscription mirror reports `canWrite=false`. The banner:
 *
 *   • Never blocks the UI — reads still work.
 *   • Prominently invites the owner to /billing.
 *   • Non-owner users see a "contact the account owner" copy.
 *   • Auto-hides on /billing, /pricing, /billing/success, /billing/cancel
 *     so it doesn't shout at the user while they are recovering.
 *
 * Reason-specific messaging maps `entitlement.reason` from the backend
 * resolver to a friendly copy string.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, CreditCard, ArrowRight } from 'lucide-react';

import { useEntitlement } from '../hooks/useEntitlement';
import { useRole } from '../context/RoleContext';

const HIDE_ON_PATHS = new Set([
  '/billing',
  '/billing/success',
  '/billing/cancel',
  '/pricing',
  '/login',
  '/signup',
]);

function messageFor(reason, isOwner) {
  const base = {
    no_subscription: {
      title: 'No active subscription',
      body:  isOwner
        ? 'Pick a plan to start creating leads, jobs, invoices and messages.'
        : 'Ask the account owner to pick a plan so your team can create new work.',
    },
    trialing: null,
    active:   null,
    grandfathered: null,
    grace: null,
    grace_expired: {
      title: 'Payment overdue',
      body:  isOwner
        ? 'Your last payment failed. Update billing to keep creating new work.'
        : 'Payment is overdue. Ask the account owner to update billing.',
    },
    canceled: {
      title: 'Subscription cancelled',
      body:  isOwner
        ? 'Your CleanBid subscription is cancelled. Reactivate to resume creating new work.'
        : 'The subscription is cancelled. Ask the account owner to reactivate.',
    },
    unpaid: {
      title: 'Subscription unpaid',
      body:  isOwner
        ? 'Payment is required. Existing data is safe — update billing to keep working.'
        : 'Payment is required. Ask the account owner to update billing.',
    },
    incomplete: {
      title: 'Checkout not completed',
      body:  isOwner
        ? 'Finish your checkout to activate CleanBid.'
        : 'Checkout is incomplete. Ask the account owner to complete billing setup.',
    },
  };
  return base[reason] || null;
}

export default function BillingReadOnlyBanner() {
  const { canWrite, reason, loading } = useEntitlement();
  const { role } = useRole();
  const { pathname } = useLocation();

  if (loading) return null;
  if (canWrite) return null;
  if (HIDE_ON_PATHS.has(pathname)) return null;

  const isOwner = role === 'owner';
  const msg = messageFor(reason, isOwner);
  if (!msg) return null;

  return (
    <div
      className="sticky top-0 z-40 border-b border-amber-300 bg-amber-50 text-amber-900"
      data-testid="billing-readonly-banner"
      role="status"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-start sm:items-center gap-3 flex-col sm:flex-row">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden />
        <div className="flex-1 text-sm">
          <b data-testid="billing-readonly-banner-title">{msg.title}.</b>{' '}
          <span data-testid="billing-readonly-banner-body">{msg.body}</span>
        </div>
        {isOwner ? (
          <Link
            to="/billing"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-amber-900 text-white px-3 py-1.5 text-xs font-medium hover:bg-amber-800 transition-colors"
            data-testid="billing-readonly-banner-cta"
          >
            <CreditCard className="h-3.5 w-3.5" />
            Update billing
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        ) : null}
      </div>
    </div>
  );
}
